// Removed stray template fragment injected accidentally during refactor
import { LitElement, html, unsafeCSS } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { customElement, state } from 'lit/decorators.js';
import {
  listModulesByGroup,
  ModulesHubLayout,
  ResolvedModule,
  ResolvedModuleGroup,
  AccessLike,
  ModuleCategory,
  ModuleSubmenuItem,
} from '../../modules/registry/module-registry';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
// Real .css file imported as text; unsafeCSS wraps it as the CSSResult Lit
// adopts into this component's shadow root. See data-table.ts for the rationale.
import styles from './modules-hub-styles.css?inline';
import '../../components/search/agwa-search';
import { DomainId, GlassAccreditationRoles } from '../../utility/claim-id';

@customElement('modules-hub')
export class ModulesHub extends WithUserProfileConsumer(LitElement) {
  static styles = unsafeCSS(styles);

  @state() private layout: ModulesHubLayout = {
    groups: [],
    categories: {
      core: [],
      tools: [],
      documents: [],
      maintenance: [],
      audit: [],
      admin: [],
      other: [],
    },
  };
  @state() private search: string = '';
  // Removed category pills bar feature; keeping state only if reintroduced later would be YAGNI.
  private _searchInput?: HTMLElement;
  private _lastFocusedId?: string; // persist last focused card within session
  private _hadFocusedCard = false; // track if focus lived inside cards previously

  connectedCallback(): void {
    super.connectedCallback();
    const saved = localStorage.getItem('modulesHub.search');
    if (saved) this.search = saved;
    this.refresh();
    window.addEventListener('keydown', this._onGlobalKeyDown);
  }

  disconnectedCallback(): void {
    window.removeEventListener('keydown', this._onGlobalKeyDown);
    super.disconnectedCallback();
  }

  private _onGlobalKeyDown = (e: KeyboardEvent) => {
    if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const active = document.activeElement as HTMLElement | null;
      if (
        active &&
        (active.tagName === 'INPUT' ||
          active.getAttribute('contenteditable') === 'true')
      )
        return;
      e.preventDefault();
      this._searchInput?.focus();
    }
    // ArrowDown from search input focuses first result
    if (e.key === 'ArrowDown') {
      const active = (this.getRootNode() as Document | ShadowRoot)
        .activeElement as HTMLElement | null;
      if (active && active === this._searchInput) {
        const first = this.renderRoot?.querySelector(
          '.grid.unified a.card-link'
        ) as HTMLElement | null;
        if (first) {
          e.preventDefault();
          first.focus();
          return;
        }
      }
    }
    // Arrow navigation when focus is within unified results
    if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      const container = this.renderRoot?.querySelector(
        '.grid.unified'
      ) as HTMLElement | null;
      if (!container) return;
      const cards = Array.from(
        container.querySelectorAll<HTMLElement>('a.card-link')
      );
      if (!cards.length) return;
      const active = (this.getRootNode() as Document | ShadowRoot)
        .activeElement as HTMLElement | null;
      let idx = cards.indexOf(active as HTMLElement);
      if (idx === -1) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          cards[0].focus();
        }
        return;
      }
      // Determine columns dynamically based on first two row card offsets
      let cols = 1;
      const firstTop = cards[0].getBoundingClientRect().top;
      for (let i = 1; i < cards.length; i++) {
        const t = cards[i].getBoundingClientRect().top;
        if (t !== firstTop) {
          cols = i;
          break;
        }
      }
      if (cols < 1) cols = 1;
      let next = idx;
      switch (e.key) {
        case 'ArrowRight':
          next = Math.min(cards.length - 1, idx + 1);
          break;
        case 'ArrowLeft':
          next = Math.max(0, idx - 1);
          break;
        case 'ArrowDown':
          next = Math.min(cards.length - 1, idx + cols);
          break;
        case 'ArrowUp':
          next = Math.max(0, idx - cols);
          break;
      }
      if (next !== idx) {
        e.preventDefault();
        cards[next].focus();
      }
    }
    // Home/End/PageUp/PageDown support in unified results
    if (['Home', 'End', 'PageUp', 'PageDown'].includes(e.key)) {
      const container = this.renderRoot?.querySelector(
        '.grid.unified'
      ) as HTMLElement | null;
      if (!container) return;
      const cards = Array.from(
        container.querySelectorAll<HTMLElement>('a.card-link')
      );
      if (!cards.length) return;
      const active = (this.getRootNode() as Document | ShadowRoot)
        .activeElement as HTMLElement | null;
      let idx = cards.indexOf(active as HTMLElement);
      if (idx === -1) return; // only act when inside results
      let next = idx;
      const viewport = container.getBoundingClientRect();
      const cardHeight = cards[idx].getBoundingClientRect().height;
      const approxPerPage = Math.max(
        1,
        Math.floor(viewport.height / cardHeight) - 1
      );
      switch (e.key) {
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = cards.length - 1;
          break;
        case 'PageDown':
          next = Math.min(cards.length - 1, idx + approxPerPage);
          break;
        case 'PageUp':
          next = Math.max(0, idx - approxPerPage);
          break;
      }
      if (next !== idx) {
        e.preventDefault();
        cards[next].focus();
      }
    }
  };

  private highlight(text: string, q: string) {
    if (!q) return text;
    const safe = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(${safe})`, 'ig');
    const parts = text.split(re);
    if (parts.length === 1) return text;
    return parts.map((p) => (re.test(p) ? html`<mark>${p}</mark>` : p));
  }

  /**
   * Filter submenu items based on access requirements.
   * Items with requiresCreator=true are only shown to users with
   * glass processor domain or creator role.
   */
  private filterSubmenu(items: ModuleSubmenuItem[]): ModuleSubmenuItem[] {
    const access = this.access;
    const canCreate =
      access?.hasDomain(DomainId.glassProcessor) ||
      access?.hasRole(GlassAccreditationRoles.Creator) ||
      access?.hasRole('admin'); // Admin can always see everything

    return items.filter((item) => !item.requiresCreator || canCreate);
  }

  private refresh() {
    const accessInfo: AccessLike = {
      access: this.access,
      isLoggedIn: this.isAuthenticated,
    };
    const raw = listModulesByGroup(accessInfo);
    if (!this.search) {
      this.layout = raw;
      return;
    }
    const q = this.search.trim().toLowerCase();
    const matches = (m: ResolvedModule) => {
      const title = m.title.toLowerCase();
      const desc = (m.description || '').toLowerCase();
      const cat = (m.category || '').toLowerCase();
      const id = m.id.toLowerCase();
      const route = m.route.toLowerCase();
      return (
        title.includes(q) ||
        desc.includes(q) ||
        cat.includes(q) ||
        id.startsWith(q) ||
        route.includes(q)
      );
    };

    const filteredGroups: ResolvedModuleGroup[] = raw.groups
      .map((g) => ({ ...g, modules: g.modules.filter(matches) }))
      .filter((g) => g.modules.length > 0);

    const filteredCategories: Record<ModuleCategory, ResolvedModule[]> = {
      core: [],
      tools: [],
      documents: [],
      maintenance: [],
      audit: [],
      admin: [],
      other: [],
    };
    (Object.keys(raw.categories) as ModuleCategory[]).forEach((k) => {
      filteredCategories[k] = raw.categories[k].filter(matches);
    });

    this.layout = { groups: filteredGroups, categories: filteredCategories };
  }

  private renderModuleCard(m: ResolvedModule) {
    const q = this.search;
    const showCategoryBadge = this.search.trim().length > 0;
    const hasSubmenu = m.submenu && m.submenu.length > 0;

    // If no submenu, render as a single clickable card
    if (!hasSubmenu) {
      return html`<a
        class="card-link"
        href="/${m.route}"
        @focus=${() => this._onCardFocus(m.id)}
      >
        <div class="card" part="card" data-module-id=${m.id}>
          <div class="title-banner">${this.highlight(m.title, q)}</div>
          <div
            class="hero-media"
            aria-hidden=${m.heroGraphic ? 'false' : 'true'}
          >
            ${
              m.heroGraphic
                ? html`<img
                    src="${m.heroGraphic}"
                    alt="${m.title} graphic"
                    loading="lazy"
                  />`
                : m.iconName
                  ? html`<sl-icon name="${m.iconName}"></sl-icon>`
                  : html``
            }
          </div>
          <div class="body">
            <p>${this.highlight(m.description, q)}</p>
          </div>
          <div class="meta-row">
            ${
              showCategoryBadge
                ? html`<span class="badge">${m.category}</span>`
                : null
            }
            ${m.beta ? html`<span class="badge beta">Beta</span>` : html``}
          </div>
        </div>
      </a>`;
    }

    // With submenu, render card with clickable header and separate submenu links
    return html`
      <div class="card" part="card" data-module-id=${m.id}>
        <a
          class="card-link"
          href="/${m.route}"
          @focus=${() => this._onCardFocus(m.id)}
          style="text-decoration: none; color: inherit;"
        >
          <div class="title-banner">${this.highlight(m.title, q)}</div>
          <div
            class="hero-media"
            aria-hidden=${m.heroGraphic ? 'false' : 'true'}
          >
            ${
              m.heroGraphic
                ? html`<img
                    src="${m.heroGraphic}"
                    alt="${m.title} graphic"
                    loading="lazy"
                  />`
                : m.iconName
                  ? html`<sl-icon name="${m.iconName}"></sl-icon>`
                  : html``
            }
          </div>
          <div class="body">
            <p>${this.highlight(m.description, q)}</p>
          </div>
        </a>
        <div class="submenu-divider"></div>
        <div class="submenu">
          ${this.filterSubmenu(m.submenu!).map(
            (item) => html`
              <a
                class="submenu-item"
                href="/${item.route}"
                @click=${(e: Event) => e.stopPropagation()}
              >
                ${
                  item.iconName
                    ? html`<sl-icon name="${item.iconName}"></sl-icon>`
                    : null
                }
                <span>${item.label}</span>
              </a>
            `
          )}
        </div>
        <div class="meta-row">
          ${
            showCategoryBadge
              ? html`<span class="badge">${m.category}</span>`
              : null
          }
          ${m.beta ? html`<span class="badge beta">Beta</span>` : html``}
        </div>
      </div>
    `;
  }

  private renderCategory(
    label: string,
    modules: ResolvedModule[],
    hideHeaders: boolean
  ) {
    if (!modules.length) return null;
    return html`<section>
      ${hideHeaders ? null : html`<h2 class="category">${label}</h2>`}
      <div class="grid">${modules.map((m) => this.renderModuleCard(m))}</div>
    </section>`;
  }

  private renderModuleGroup(group: ResolvedModuleGroup) {
    if (!group.modules.length) return null;
    return html`<section class="module-group" part="module-group">
      <h2 class="category module-group-title">
        ${
          group.iconName
            ? html`<sl-icon name=${group.iconName}></sl-icon>`
            : null
        }
        <span>${group.title}</span>
      </h2>
      ${
        group.description
          ? html`<p class="module-group-description">${group.description}</p>`
          : null
      }
      <div class="grid">
        ${group.modules.map((m) => this.renderModuleCard(m))}
      </div>
    </section>`;
  }

  render() {
    // Gather a flat list for pills if needed
    const trimmed = this.search.trim();
    const searching = trimmed.length > 0;
    // Determine if there are any matches across groups and categories when searching
    const groupsHaveMatches = this.layout.groups.some(
      (g) => g.modules.length > 0
    );
    const categoriesHaveMatches = Object.values(this.layout.categories).some(
      (arr) => arr.length > 0
    );
    const anyMatches = groupsHaveMatches || categoriesHaveMatches;
    const hideCategoryHeaders = searching && anyMatches;

    // If searching with matches, flatten all modules into one array for a unified grid
    let flattened: ResolvedModule[] = [];
    if (hideCategoryHeaders) {
      for (const g of this.layout.groups)
        flattened = flattened.concat(g.modules);
      for (const arr of Object.values(this.layout.categories)) {
        flattened = flattened.concat(arr);
      }
      // Sort by title (case-insensitive)
      flattened.sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
      );
    }

    return html`<div class="hub-container">
      <div class="hub-search">
        <agwa-search
          .value=${this.search}
          placeholder="Search"
          @search-input=${(e: CustomEvent) => {
            // live updating for responsiveness
            this.search = e.detail.value;
            this.refresh();
          }}
          @search-change=${(e: CustomEvent) => {
            this.search = e.detail.value;
            this.refresh();
            if (this.search) {
              localStorage.setItem('modulesHub.search', this.search);
            } else {
              localStorage.removeItem('modulesHub.search');
            }
          }}
          @search-clear=${() => {
            this.search = '';
            this.refresh();
            localStorage.removeItem('modulesHub.search');
          }}
          ${ref((el) => {
            const inputEl = (
              el as HTMLElement | null
            )?.shadowRoot?.querySelector('sl-input');
            this._searchInput =
              (inputEl as unknown as HTMLElement) || undefined;
          })}
        ></agwa-search>
      </div>

      ${
        searching && !anyMatches
          ? html`<section class="no-results" aria-live="polite">
              <p class="results-summary">No modules match "${trimmed}".</p>
            </section>`
          : hideCategoryHeaders
            ? html`<section>
                <p class="results-summary">
                  Showing ${flattened.length}
                  result${flattened.length === 1 ? '' : 's'} for "${trimmed}"
                </p>
                <div
                  class="grid unified"
                  part="search-results"
                  aria-label="Search results"
                  role="list"
                >
                  ${flattened.map((m) => this.renderModuleCard(m))}
                </div>
              </section>`
            : html`${this.layout.groups.map((g) => this.renderModuleGroup(g))}
              ${this.renderCategory(
                'Core',
                this.layout.categories.core || [],
                false
              )}
              ${this.renderCategory(
                'Documents',
                this.layout.categories.documents || [],
                false
              )}
              ${this.renderCategory(
                'Tools',
                this.layout.categories.tools || [],
                false
              )}
              ${this.renderCategory(
                'Maintenance',
                this.layout.categories.maintenance || [],
                false
              )}
              ${this.renderCategory(
                'Audit',
                this.layout.categories.audit || [],
                false
              )}
              ${this.renderCategory(
                'Admin',
                this.layout.categories.admin || [],
                false
              )}
              ${this.renderCategory(
                'Other',
                this.layout.categories.other || [],
                false
              )}`
      }
    </div>`;
  }

  private _onCardFocus(id: string) {
    this._lastFocusedId = id;
    this._hadFocusedCard = true;
  }

  protected updated(changed: Map<string, unknown>): void {
    super.updated(changed);
    // Attempt to restore focus to last focused card if rerender replaced DOM
    if (!this._hadFocusedCard || !this._lastFocusedId) return;
    const active = (this.getRootNode() as Document | ShadowRoot)
      .activeElement as HTMLElement | null;
    // Do not steal focus from search input while user typing
    if (active && active === this._searchInput) return;
    // If active element already a card-link inside this component, keep it
    if (
      active &&
      this.renderRoot.contains(active) &&
      active.classList.contains('card-link')
    )
      return;
    const candidate = this.renderRoot.querySelector<HTMLElement>(
      `.card-link .card[data-module-id="${this._lastFocusedId}"]`
    );
    // adjust to focus the anchor (parent of card)
    const anchor = candidate?.closest<HTMLAnchorElement>('a.card-link');
    if (anchor) anchor.focus();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'modules-hub': ModulesHub;
  }
}
